import React, {useState} from "react";
import {Button} from "primereact/button";
import {Checkbox} from "primereact/checkbox";
import {Dialog} from "primereact/dialog";
import {Dropdown} from "primereact/dropdown";
import {InputSwitch} from "primereact/inputswitch";
import {InputText} from "primereact/inputtext";
import {Tag} from "primereact/tag";
import Icon from "../../system/components/icon";
import {notificationApi} from "../../models/notification/notification-api";
import {DAYS} from "./quiet-hours-section";
import TimeSelect from "./time-select";

// Scheduled reports (spec §22-28). Reports save on their own (immediate), not through the page's save bar.

const FREQUENCIES = [
    {label: "هر روز", value: "daily"},
    {label: "روزهای کاری (شنبه تا چهارشنبه)", value: "workdays"},
    {label: "هفتگی", value: "weekly"},
];
const CHANNELS = [
    {label: "داخل زوفینو", value: "in_app"},
    {label: "زوفینو + Push مرورگر", value: "push"},
];
const SCOPES = [
    {label: "کل این فضای کاری", value: "current_workspace"},
    {label: "فقط مواردی که مستقیماً به من مربوط است", value: "directly_related_only"},
];
const EDITABLE = ["name", "reportType", "enabled", "frequency", "time", "dayOfWeek", "scope", "channels", "sections"];
const ICONS = {morning: "wb_sunny", end_of_day: "nights_stay", weekly: "calendar_view_week", custom: "summarize"};

export function reportSummary(report) {
    const when = report.frequency === "weekly"
        ? `${DAYS.find((day) => day.key === report.dayOfWeek)?.label || "شنبه"} ${report.time}`
        : `${FREQUENCIES.find((item) => item.value === report.frequency)?.label.replace(/ \(.*\)$/, "") || ""} ${report.time}`;
    const channel = (report.channels || []).includes("browser_push") ? "زوفینو + Push" : "داخل زوفینو";
    return `${when} · ${channel}`;
}

function ReportEditor({report, sections, workspace, onSaved, onDeleted, toast}) {
    const [draft, setDraft] = useState(report);
    const [busy, setBusy] = useState(null);
    const [expanded, setExpanded] = useState(!report.documentId);
    const change = (patch) => setDraft((current) => ({...current, ...patch}));
    const fields = (value) => JSON.stringify(EDITABLE.map((field) => value?.[field] ?? null));
    const dirty = fields(draft) !== fields(report);
    const selected = new Set(draft.sections || []);

    const run = async (key, action) => {
        if (busy) return;
        setBusy(key);
        try {
            await action();
        } catch (error) {
            toast("error", "ذخیره گزارش انجام نشد. دوباره تلاش کنید.");
        } finally {
            setBusy(null);
        }
    };
    const payload = () => Object.fromEntries(EDITABLE.map((field) => [field, draft[field]]));
    const save = () => run("save", async () => {
        const saved = draft.documentId
            ? await notificationApi.updateReport(draft.documentId, payload())
            : await notificationApi.createReport(payload(), workspace?.documentId);
        onSaved(report, saved);
        toast("success", "گزارش ذخیره شد.");
    });
    const remove = () => run("delete", async () => {
        if (draft.documentId) await notificationApi.deleteReport(draft.documentId);
        onDeleted(report);
    });
    const sendNow = () => run("send", async () => {
        await notificationApi.sendReport(draft.documentId);
        toast("success", "گزارش ساخته شد؛ در «اعلان‌ها» ببینید.");
    });

    return (
        <div className="border-1 border-200 border-round-xl overflow-hidden">
            <div className="flex align-items-center justify-content-between gap-2 p-3">
                <button type="button" className="ns-plain-button flex align-items-center gap-3 p-0 flex-1 min-w-0" onClick={() => setExpanded(!expanded)}>
                    <span className="w-2rem h-2rem border-circle surface-100 flex flex-shrink-0 align-items-center justify-content-center text-primary"><Icon icon={`mi mi:${ICONS[draft.reportType] || ICONS.custom}`}/></span>
                    <span className="flex flex-column gap-1 min-w-0">
                        <strong className="text-sm text-900">{draft.name || "گزارش جدید"}{!draft.documentId && <Tag value="ذخیره نشده" severity="warning" className="mr-2" rounded/>}</strong>
                        <small className="text-xs text-500">{reportSummary(draft)}</small>
                    </span>
                </button>
                <InputSwitch checked={draft.enabled !== false} onChange={(event) => change({enabled: event.value})} aria-label="فعال"/>
                <i className={`pi pi-chevron-down text-500 cursor-pointer ${expanded ? "rotate-180" : ""}`} onClick={() => setExpanded(!expanded)}/>
            </div>
            {expanded && (
                <div className="border-top-1 border-100 p-3 flex flex-column gap-3">
                    <div className="grid m-0 text-xs">
                        <label className="col-12 md:col-6 flex flex-column gap-1 p-1">نام گزارش
                            <InputText value={draft.name || ""} onChange={(event) => change({name: event.target.value})} className="text-xs p-2" maxLength={120}/>
                        </label>
                        <label className="col-6 md:col-3 flex flex-column gap-1 p-1">زمان ارسال
                            <TimeSelect value={draft.time || "08:00"} onChange={(time) => change({time})} ariaLabel="زمان ارسال"/>
                        </label>
                        <label className="col-6 md:col-3 flex flex-column gap-1 p-1">تکرار
                            <Dropdown value={draft.frequency} options={FREQUENCIES} onChange={(event) => change({frequency: event.value})} className="text-xs"/>
                        </label>
                        {draft.frequency === "weekly" && (
                            <label className="col-6 md:col-3 flex flex-column gap-1 p-1">روز ارسال
                                <Dropdown value={draft.dayOfWeek} options={DAYS.map((day) => ({label: day.label, value: day.key}))} onChange={(event) => change({dayOfWeek: event.value})} className="text-xs"/>
                            </label>
                        )}
                        <label className="col-6 md:col-4 flex flex-column gap-1 p-1">کانال ارسال
                            <Dropdown
                                value={(draft.channels || []).includes("browser_push") ? "push" : "in_app"}
                                options={CHANNELS}
                                onChange={(event) => change({channels: event.value === "push" ? ["in_app", "browser_push"] : ["in_app"]})}
                                className="text-xs"
                            />
                        </label>
                        <label className="col-12 md:col-5 flex flex-column gap-1 p-1">دامنه گزارش
                            <Dropdown value={draft.scope} options={SCOPES} onChange={(event) => change({scope: event.value})} className="text-xs"/>
                        </label>
                    </div>
                    <div>
                        <div className="text-xs font-bold mb-2">چه چیزهایی داخل گزارش باشد؟</div>
                        <div className="flex flex-wrap gap-3">
                            {Object.entries(sections || {}).map(([key, label]) => (
                                <label key={key} className="flex align-items-center gap-2 text-xs cursor-pointer">
                                    <Checkbox
                                        checked={selected.has(key)}
                                        onChange={(event) => change({sections: Object.keys(sections).filter((item) => (item === key ? event.checked : selected.has(item)))})}
                                    />
                                    {label}
                                </label>
                            ))}
                        </div>
                    </div>
                    <div className="flex flex-wrap align-items-center gap-2">
                        <Button label="ذخیره گزارش" icon="pi pi-check" size="small" className="text-xs gap-1" loading={busy === "save"} disabled={Boolean(busy) || (!dirty && Boolean(draft.documentId)) || !(draft.sections || []).length} onClick={save}/>
                        {draft.documentId && <Button label="ارسال آزمایشی اکنون" icon="pi pi-send" size="small" text className="text-xs gap-1" loading={busy === "send"} disabled={Boolean(busy) || dirty} onClick={sendNow}/>}
                        <Button label="حذف" icon="pi pi-trash" size="small" text severity="danger" className="text-xs gap-1 mr-auto" loading={busy === "delete"} disabled={Boolean(busy)} onClick={remove}/>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function ReportsSection({reportsData, workspace, onReportsChange, toast}) {
    const [visible, setVisible] = useState(false);
    const [drafts, setDrafts] = useState([]);
    const reports = reportsData?.reports || [];
    const templates = reportsData?.templates || [];
    const sections = reportsData?.sections || {};
    const usedTypes = new Set([...reports, ...drafts].map((report) => report.reportType));

    const addFrom = (template) => {
        setDrafts((current) => [...current, {
            key: `${Date.now()}-${Math.random()}`,
            enabled: true,
            timezone: "Asia/Tehran",
            scope: "current_workspace",
            channels: ["in_app"],
            ...(template || {reportType: "custom", name: "گزارش جدید", frequency: "daily", time: "09:00", dayOfWeek: "sat", sections: ["my_tasks_today"]}),
        }]);
    };
    const onSaved = (before, saved) => {
        if (before.documentId) onReportsChange(reports.map((report) => (report.documentId === saved.documentId ? saved : report)));
        else {
            setDrafts((current) => current.filter((draft) => draft !== before));
            onReportsChange([...reports, saved]);
        }
    };
    const onDeleted = (report) => {
        if (report.documentId) onReportsChange(reports.filter((item) => item.documentId !== report.documentId));
        else setDrafts((current) => current.filter((draft) => draft !== report));
    };

    return (
        <section className="ns-card h-full">
            <div className="flex align-items-start justify-content-between gap-3">
                <div>
                    <h2 className="ns-card-title"><span className="ns-icon"><i className="pi pi-calendar-clock"/></span>گزارش‌های زمان‌بندی‌شده <Tag value="اختیاری" severity="secondary" rounded/></h2>
                    <p className="ns-card-description mb-0">به‌جای چند اعلان پراکنده، یک گزارش خلاصه در ساعت مشخص دریافت کنید. گزارش‌ها مخصوص فضای کاری «{workspace?.name || "فعلی"}» هستند و فقط همین فضای کاری را پوشش می‌دهند.</p>
                </div>
            </div>
            <div className="flex flex-column gap-2 mt-3">
                {reports.length ? reports.map((report) => (
                    <div key={report.documentId} className="flex align-items-center justify-content-between gap-3 p-3 border-round-xl border-1 border-200">
                        <span className="flex align-items-center gap-3 min-w-0">
                            <Icon icon={`mi mi:${ICONS[report.reportType] || ICONS.custom}`} className="text-primary text-xl"/>
                            <span className="flex flex-column gap-1 min-w-0">
                                <strong className="text-sm">{report.name}</strong>
                                <small className="text-xs text-500">{reportSummary(report)}</small>
                            </span>
                        </span>
                        <Tag value={report.enabled ? "فعال" : "غیرفعال"} severity={report.enabled ? "success" : "secondary"} rounded/>
                    </div>
                )) : (
                    <div className="text-xs text-500 p-3 border-round-xl surface-50">هنوز گزارشی نساخته‌اید. مثلاً «گزارش شروع روز» هر صبح کارهای امروز، عقب‌افتاده‌ها و سررسیدهای نزدیک را یک‌جا می‌فرستد.</div>
                )}
            </div>
            <Button label="مدیریت گزارش‌های زمان‌بندی‌شده" icon={<Icon icon="mi mi:edit_calendar" className="ml-1"/>} size="small" outlined className="text-xs gap-1 mt-3" onClick={() => setVisible(true)}/>

            <Dialog
                header="گزارش‌های زمان‌بندی‌شده"
                visible={visible}
                onHide={() => { setVisible(false); setDrafts([]); }}
                className="rtl notification-settings notification-settings-dialog w-full md:w-8 lg:w-7"
                style={{maxWidth: "56rem"}}
                breakpoints={{"768px": "100vw"}}
                position="center"
                dismissableMask
            >
                <div className="flex flex-column gap-3 text-xs">
                    <div className="surface-50 border-round-xl p-3 line-height-3">
                        <strong className="block mb-1">گزارش زمان‌بندی‌شده با اعلان لحظه‌ای فرق دارد</strong>
                        مثلاً به‌جای چند اعلان جداگانه درباره کارهای امروز، زوفینو ساعت ۸ صبح یک گزارش کوتاه شامل کارهای امروز، موارد عقب‌افتاده و سررسیدهای نزدیک برایتان می‌فرستد. گزارش خالی ارسال نمی‌شود.
                    </div>
                    {[...reports, ...drafts].map((report) => (
                        <ReportEditor key={report.documentId || report.key} report={report} sections={sections} workspace={workspace} onSaved={onSaved} onDeleted={onDeleted} toast={toast}/>
                    ))}
                    <div className="flex flex-wrap gap-2">
                        {templates.filter((template) => !usedTypes.has(template.reportType)).map((template) => (
                            <Button key={template.reportType} label={`افزودن ${template.name}`} icon="pi pi-plus" size="small" outlined className="text-xs gap-1" onClick={() => addFrom(template)}/>
                        ))}
                        <Button label="ساخت گزارش جدید" icon="pi pi-plus" size="small" text className="text-xs gap-1" onClick={() => addFrom(null)} disabled={reports.length + drafts.length >= 10}/>
                    </div>
                </div>
            </Dialog>
        </section>
    );
}
